import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { selectCatalog } from '../root-store/catalog-store/selectors';
import { RootState } from '../root-store/root-state.model';
import { selectOrderIdQueryString, selectOrderIdStatusPage } from '../root-store/router/selectors';
import { LoginService } from '../services/login.service';

const keepAliveUrl = '/api/Cart/KeepAlive';

export const CATALOG_ID_HEADER = new HttpContextToken(() => 0);

// Set CatalogId HTTP-Param
export function webApiInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const store = inject(Store<RootState>);
  const loginService = inject(LoginService);
  const snackbar = inject(MatSnackBar);
  const translate = inject(TranslateService);

  let catalogId = store.selectSignal(selectCatalog)()?.id;

  if (!catalogId) {
    return next(req);
  }

  // override CatalogId from HttpContext, if specified
  const overrideCatalogId = req.context.get(CATALOG_ID_HEADER);
  if (overrideCatalogId > 0) {
    catalogId = overrideCatalogId;
  }

  const ignoreCatalogIdQueryParam =
    req.params.has('CatalogId') || req.url.startsWith('i18n/') || req.url.startsWith('icons/');

  let modified = ignoreCatalogIdQueryParam
    ? req
    : req.clone({
        // CatalogId is set as QueryString. It would be nicer as HttpHeader, but this causes caching issues in GoogleBot.
        setParams: {
          CatalogId: catalogId.toString(),
        },
      });

  if (modified.method !== 'GET') {
    modified = modified.clone({
      setHeaders: {
        RequestVerificationToken: loginService.requestVerificationToken!,
      },
    });
  }

  // optionally add OrderId-Param on requests to /api/Cart, if specified in QueryString (except for /KeepAlive)
  if (!modified.params.has('OrderId')) {
    if (req.url.includes('/api/Cart') && !req.url.includes(keepAliveUrl)) {
      const orderIdQueryString = store.selectSignal(selectOrderIdQueryString)();
      const orderIdRouteParam = store.selectSignal(selectOrderIdStatusPage)();

      if (orderIdQueryString) {
        modified = modified.clone({ setParams: { OrderId: orderIdQueryString } });
      } else if (orderIdRouteParam) {
        modified = modified.clone({ setParams: { OrderId: orderIdRouteParam } });
      }
    }
  }

  return next(modified).pipe(
    // handle Response-Errors
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        let message: string = error.error?.toString();

        // If anti-forgery token is invalid, show an understandable message and reload the page
        if (message?.includes('XSRF')) {
          message = translate.instant(_('ERROR.XSRF_DIFFERENT_USER'));

          if (modified.url.includes(keepAliveUrl)) {
            document.location.reload();
          }
        }

        if (message) {
          snackbar.open(message);
        }
      }

      return throwError(() => error);
    }),
  );
}
